import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import Flex from "../../../../../../../components/Flex";
import { ScrollArea } from "../../../../../../../components/ScrollBar";
import Text from "../../../../../../../components/Text";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import {
  CHART_COLORS,
  CHART_TYPOGRAPHY,
} from "../../../../../../../components/app/provider/patient/chart";
import { PatientDocumentIcon } from "../../../../../../../components/svg-icons/PatientSidebarIcons";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import { useRoleController } from "../../../../../../../components/useRoleController";
import { usePatientAllergyList } from "../../../../../../../queries/allergies/use-patient-allergies-list";
import { usePatientCarePlanList } from "../../../../../../../queries/care-plan/use-patient-careplan-list";
import { usePatientDiagnosesList } from "../../../../../../../queries/diagnoses/use-patient-diagnoses-list";
import { usePastFamilyHistoryList } from "../../../../../../../queries/history/past-family/use-patient-family-history-list";
import { usePastMedicalHistoryList } from "../../../../../../../queries/history/past-medication/use-patient-medical-history-list";
import { usePastSurgicalHistoryList } from "../../../../../../../queries/history/past-surgical/use-past-surgical-history-list";
import { usePatientMedicationList } from "../../../../../../../queries/medication/use-patient-medication-list";
import { usePatientVaccinesList } from "../../../../../../../queries/vaccines/use-patient-vaccines";
import { usePatientLatestVitalsByPatientId } from "../../../../../../../queries/vitals/use-patient-latest-vitals-by-patient-id";
import { getXtenantId, roleFilter } from "../../../../../../../utils/utils";
import PatientRecords from "./PatientRecords";
import { RecordsScrollProvider } from "./encounterSummaryComponents/RecordsScrollContext";
import { paginationForSideBar } from "./encounterSummaryComponents/SummaryConstant";

const PatientClinicalRecords = ({ patientId }: { patientId: string }) => {
  const AllSectionsArr = Array.from({ length: 7 })?.map(() => false);
  const { hasNone } = useRoleController("Clinical");

  const SECTIONS = [
    { label: "Allergies" },
    { label: "Diagnosis" },
    { label: "Medications" },
    { label: "Vaccines" },
    { label: "Vitals" },
    { label: "History" },
    { label: "Care_Plan" },
  ];

  const [accordionExpanded, setAccordionExpanded] =
    useState<boolean[]>(AllSectionsArr);

  // ECRH-4226: track scroll state of the Records container so that hover
  // tooltips on individual records can be suppressed while scrolling. This
  // prevents the MUI Tooltip popper from leaving behind a stale/misaligned
  // tooltip after the user scrolls while a record is hovered.
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current !== null) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const handleRecordsScroll = () => {
    setIsScrolling(true);
    if (scrollTimeoutRef.current !== null) {
      window.clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = window.setTimeout(() => {
      setIsScrolling(false);
    }, 150);
  };

  const isExpandAll = accordionExpanded.every((it) => !!it);

  const sectionSelectionState = SECTIONS.reduce(
    (acc, section, idx) => {
      acc[section.label.replace(" ", "_")] = accordionExpanded[idx] === true;
      return acc;
    },
    {} as Record<string, boolean>
  );
  // console.log("sectionSelectionState - ", sectionSelectionState);

  const { data: diagnosisList, isLoading: isDiagnosisLoading } =
    usePatientDiagnosesList({
      patientUuid: patientId,
      pagination: paginationForSideBar,
      archive: false,
      enabled: sectionSelectionState.Diagnosis,
    });

  const { data: allergyList, isLoading: isAllergyLoading } =
    usePatientAllergyList({
      patientUuid: patientId ?? "",
      pagination: paginationForSideBar,
      gridSorting: [],
      archive: false,
      enabled: sectionSelectionState.Allergies,
    });

  // note : we are showing only one vital for now in ascending order so that recent vital will be shown first
  const { data: vitalsList, isLoading: isVitalsLoading } =
    usePatientLatestVitalsByPatientId({
      patientUuid: patientId,
      xTENANTID: getXtenantId(),
      enabled: sectionSelectionState.Vitals,
    });
  const { data: medicationsList, isLoading: isMedicationsLoading } =
    usePatientMedicationList({
      patientUuid: patientId,
      pagination: paginationForSideBar,
      archive: false,
      active: true,
      enabled: sectionSelectionState.Medications,
    });

  const { data: vaccinesList, isLoading: isVaccinesLoading } =
    usePatientVaccinesList({
      patientUuid: patientId,
      pagination: paginationForSideBar,
      archive: false,
      enabled: sectionSelectionState.Vaccines,
    });

  const { data: carePlanList, isLoading: isCarePlanLoading } =
    usePatientCarePlanList({
      patientUuid: patientId,
      pagination: paginationForSideBar,
      archive: false,
      current: true,
      enabled: sectionSelectionState.Care_Plan,
    });

  const {
    data: pastPatientMedicalHistoryList,
    isLoading: isMedicalHistoryLoading,
  } = usePastMedicalHistoryList({
    patientUuid: patientId,
    pagination: paginationForSideBar,
    archive: false,
    enabled: sectionSelectionState.History,
  });
  const { data: surgicalHistoryList, isLoading: isSurgicalHistoryLoading } =
    usePastSurgicalHistoryList({
      patientUuid: patientId,
      pagination: paginationForSideBar,
      archive: false,
      enabled: sectionSelectionState.History,
    });

  const { data: familyHistoryList, isLoading: isFamilyHistoryLoading } =
    usePastFamilyHistoryList({
      patientUuid: patientId,
      pagination: paginationForSideBar,
      archive: false,
      enabled: sectionSelectionState.History,
    });
  // Loading state object
  const loadingStates = {
    Allergies: isAllergyLoading,
    Diagnosis: isDiagnosisLoading,
    Medications: isMedicationsLoading,
    Vaccines: isVaccinesLoading,
    Vitals: isVitalsLoading,
    History: isMedicalHistoryLoading,
    Surgical_History: isSurgicalHistoryLoading,
    Family_History: isFamilyHistoryLoading,
    Care_Plan: isCarePlanLoading,
  };

  // todo: add social history list is remaining ( BA team will give design to show list )

  return (
    <Box width="100%" display="flex" flexDirection="column" height="100%">
      <Flex
        justifyContent="space-between"
        alignItems="center"
        bgcolor={CHART_COLORS.surfaceDefault}
        p="12px"
        flexShrink={0}
      >
        <Flex gap="4px" alignItems="center">
          <PatientDocumentIcon />
          <Text
            sx={{
              fontWeight: CHART_TYPOGRAPHY.weight.medium,
              fontFamily: CHART_TYPOGRAPHY.fontFamily,
              ...CHART_TYPOGRAPHY.body4,
              color: CHART_COLORS.textDark,
            }}
          >
            Patient Records
          </Text>
        </Flex>
        <StyledButtonV2
          variant="text"
          label={isExpandAll ? "Collapse All" : "Expand All"}
          eIcon={
            isExpandAll ? (
              <CloseFullscreenIcon sx={{ fontSize: "11px" }} />
            ) : (
              <OpenInFullIcon sx={{ fontSize: "11px" }} />
            )
          }
          sx={{
            color: CHART_COLORS.primary,
            ...CHART_TYPOGRAPHY.body5,
            fontFamily: CHART_TYPOGRAPHY.fontFamily,
            fontWeight: CHART_TYPOGRAPHY.weight.medium,
          }}
          onClick={() =>
            setAccordionExpanded((prev) => prev?.map(() => !isExpandAll))
          }
        />
      </Flex>

      <Box flex={1} overflow="hidden">
        <RecordsScrollProvider value={{ isScrolling }}>
          <ScrollArea style={{ height: "100%" }} onScroll={handleRecordsScroll}>
            <PatientRecords
              accordionExpanded={accordionExpanded}
              setAccordionExpanded={setAccordionExpanded}
              SECTIONS={roleFilter({
                list: SECTIONS,
                k: "label",
                compWith: {
                  Allergies: !hasNone,
                  Diagnosis: !hasNone,
                  Medications: !hasNone,
                  Vaccines: !hasNone,
                  Vitals: !hasNone,
                },
              })}
              allergyList={allergyList}
              diagnosisList={diagnosisList}
              vitalsList={vitalsList}
              medicationsList={medicationsList}
              vaccinesList={vaccinesList}
              carePlanList={carePlanList}
              medicalHistoryList={pastPatientMedicalHistoryList}
              surgicalHistoryList={surgicalHistoryList}
              familyHistoryList={familyHistoryList}
              loadingStates={loadingStates}
            />
          </ScrollArea>
        </RecordsScrollProvider>
      </Box>
    </Box>
  );
};

export default PatientClinicalRecords;
